import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Flame, Clock, ChevronRight } from "lucide-react";

/**
 * AgenticSummaryTile — Dashboard widget showing users currently in Live Sales
 * Mode + upcoming agentic follow-ups. Refreshes every 15s.
 */
export default function AgenticSummaryTile() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/agentic-summary");
      setData(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;

  const liveCount = data.live_sales_mode?.count || 0;
  const liveUsers = data.live_sales_mode?.users || [];
  const followupCount = data.followups_scheduled?.count || 0;
  const followupUsers = data.followups_scheduled?.users || [];

  const fmtRelative = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.floor((then - now) / 1000);
    if (diff < 0) {
      const past = Math.abs(diff);
      if (past < 60) return `${past}s ago`;
      if (past < 3600) return `${Math.round(past / 60)} min ago`;
      return `${Math.round(past / 3600)}h ago`;
    }
    if (diff < 60) return `in ${diff}s`;
    if (diff < 3600) return `in ${Math.round(diff / 60)} min`;
    if (diff < 86400) return `in ${Math.round(diff / 3600)}h`;
    return `in ${Math.round(diff / 86400)}d`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="agentic-summary-tile">
      {/* Live Sales Mode */}
      <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Flame className="h-4 w-4" />
            <div className="text-xs font-bold uppercase tracking-wider">Live Sales Mode</div>
          </div>
          <div className="text-white/95 text-sm font-bold tabular" data-testid="live-mode-count">
            {liveCount} active
          </div>
        </div>
        <div className="p-3">
          {liveCount === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">
              No users in Live Sales Mode right now.
            </div>
          ) : (
            <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="live-mode-users">
              {liveUsers.map((u) => (
                <Link
                  to={`/users/${u.id}`}
                  key={u.id}
                  className="group flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-orange-50 transition"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      {u.name || u.phone || u.email || "—"}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {(u.live_mode_message_count || 0)} nudges · CPS {u.conversion_score ?? "—"}
                      {u.live_mode_started_at && <> · started {fmtRelative(u.live_mode_started_at)}</>}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-orange-600 shrink-0" />
                </Link>
              ))}
            </div>
          )}
          {liveCount > 0 && (
            <Link
              to="/users?live_sales_mode=true"
              className="mt-2 block text-center text-[11px] font-semibold text-orange-700 hover:underline pt-1 border-t border-slate-100"
            >
              View all in Users →
            </Link>
          )}
        </div>
      </div>

      {/* Follow-ups */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-800 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Clock className="h-4 w-4" />
            <div className="text-xs font-bold uppercase tracking-wider">Scheduled Follow-ups</div>
          </div>
          <div className="text-white/95 text-sm font-bold tabular" data-testid="followup-count">
            {followupCount} queued
          </div>
        </div>
        <div className="p-3">
          {followupCount === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">
              No follow-ups scheduled right now.
            </div>
          ) : (
            <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="followup-users">
              {followupUsers.map((u) => (
                <Link
                  to={`/users/${u.id}`}
                  key={u.id}
                  className="group flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50 transition"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      {u.name || u.phone || "—"}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate italic">
                      {u.next_followup_reason || "no reason"}
                    </div>
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 shrink-0 tabular">
                    {fmtRelative(u.next_followup_at)}
                  </div>
                </Link>
              ))}
            </div>
          )}
          {followupCount > 0 && (
            <Link
              to="/users?has_followup=true"
              className="mt-2 block text-center text-[11px] font-semibold text-slate-700 hover:underline pt-1 border-t border-slate-100"
            >
              View all in Users →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
